import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Accordion,
  AccordionItem,
  Button,
  Checkbox,
  Modal,
  RadioGroup,
  Select,
  Stepper,
} from '@enamelai/design-system'
import { Icon as AddIcon } from '@enamelai/design-system/icons/Add'
import { Icon as ChevronRightIcon } from '@enamelai/design-system/icons/Pagination_chevron_right'
import { SchemaFormRenderer } from '@components/pages/FormBuilder/Shared/SchemaFormRenderer'
import {
  schemaJsonToGroupsWithError,
  formTemplateToFormData,
} from './formTemplateToFormData'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFormsSentToast,
  createKioskOtpGeneratedToast,
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { createPatientFormsOtp } from '@services/api/otp.api'
import {
  createFormAssignments,
  getFormTemplates,
  getPendingFormEntries,
  sendFormAssignmentsInvitation,
} from '@services/api/forms.api'
import {
  getAppointmentsSummary,
  type AppointmentSummary,
} from '@services/api/appointment.api'
import { FormTemplateStatus, type IFormTemplate } from '@dataTypes/forms.type'
import type { FormData } from '@components/pages/FormBuilder/PendingFormsTable/CompletedFormsModal.component'
import type { SendingAppointmentDetail } from './SendingFormsTable.component'
import type { PendingFormEntry } from '@pages/FormBuilder/assignedForms.types'
import { createAssignedFormEntry } from '@pages/FormBuilder/createAssignedFormEntry'
import {
  formatAppointmentLocalDate,
  getAppointmentLocalDateYmd,
} from '@utils/time.util'
import moment from 'moment'
import './AssignFormsModal.css'

export const PATIENT_SPECIFIC_FORM_TITLE = 'Patient-specific Form'

const STEPS = ['Select schedule', 'Select forms', 'Review']
const SCHEDULE_LOOKAHEAD_DAYS = 365

/** Set to true to show "Create patient-specific form" in assign forms modal (and appointments add-forms). */
const SHOW_CREATE_PATIENT_SPECIFIC_FORM = false

const DEFAULT_EMAIL_COPY = `Dear [patient],

To ensure a smooth visit for your upcoming appointment, please complete your intake paperwork through our secure portal.

How to access your forms:
• Click the link below to open the secure portal.
• Enter your invitation code when prompted.

Access Link: [Secure Link]
Invitation Code: [XXXXXX]

Note: This link and invitation code are for your protection and remain valid through the end of your appointment day.

We look forward to seeing you soon!`

const DEFAULT_SMS_COPY = `To ensure a smooth visit for your upcoming appointment, please review your intake paperwork through our secure portal.

These forms are view-only and do not require signing in the portal.

How to access your forms:
• Link: [Secure Link]
• Enter invitation code: [XXXXXX]

Note: This link and invitation code are for your protection and remain valid through the end of your appointment day.`

type SendFormsTo = 'kiosk' | 'mobile' | 'email'

export interface AppointmentSchedule {
  id: string
  date: string
  type: string
}

export type AssignFormsModalVariant = 'assign' | 'addToAppointment' | 'direct'

interface AssignFormsModalProps {
  show: boolean
  onHide: () => void
  patientName: string
  patientId?: string
  publicPatientId?: string
  patientMobile?: string
  appointments?: SendingAppointmentDetail[]
  onOTPShow?: (
    patientName: string,
    otp: string,
    otpId?: string,
    appointmentId?: string,
  ) => void
  /** Called after invitation is created with the new pending-form entry for the Pending tab */
  onFormsAssigned?: (entry: PendingFormEntry) => void
  /** When 'addToAppointment', only steps 2 (Select Forms) and 3 (Review) are shown, no stepper, custom footer */
  variant?: AssignFormsModalVariant
  /** Called when user confirms "Add forms to appointment" (variant must be 'addToAppointment') */
  onAddFormsToAppointment?: (
    selectedFormIds: string[],
    titleById: Record<string, string>,
  ) => void
  initialSelectedFormId?: string
}

const getCategorySortOrder = (categoryTitle: string): number => {
  const normalized = categoryTitle.trim().toLowerCase()
  if (normalized === 'patient information') return 0
  if (normalized === 'consent forms') return 1
  if (normalized === 'others') return 99
  return 10
}

const isUpcomingAppointment = (apt: AppointmentSummary): boolean => {
  const startStr =
    apt.appointment_start_date ?? `${apt.appt_date} ${apt.appt_start}`
  const start = new Date(startStr.replace(' ', 'T'))
  return !Number.isNaN(start.getTime()) && start >= new Date()
}

const summaryToScheduleRow = (
  apt: AppointmentSummary,
): SendingAppointmentDetail => {
  return {
    appointmentId: apt.appointment_id,
    appointmentType: apt.appointmenttype?.name ?? '—',
    roomNo: apt.operatory_id ? `Room ${apt.operatory_id.slice(0, 8)}` : '—',
    appointmentDate: getAppointmentLocalDateYmd({
      appointment_start_date: apt.appointment_start_date,
      appt_date: apt.appt_date,
    }),
    lastReminded: '',
  }
}

const AssignFormsModal: React.FC<AssignFormsModalProps> = ({
  show,
  onHide,
  patientName,
  patientId,
  publicPatientId,
  patientMobile,
  appointments = [],
  onOTPShow,
  onFormsAssigned,
  variant = 'assign',
  onAddFormsToAppointment,
  initialSelectedFormId,
}) => {
  const navigate = useNavigate()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const isAddToAppointment = variant === 'addToAppointment'
  const [isDirect, setIsDirect] = useState(variant === 'direct')
  const [sending, setSending] = useState(false)
  const [step, setStep] = useState(
    isAddToAppointment || variant === 'direct' ? 2 : 1,
  )
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<string[]>([])
  const [sendFormsTo, setSendFormsTo] = useState<SendFormsTo>('kiosk')
  const [selectedFormIds, setSelectedFormIds] = useState<string[]>([])
  const [emailCopy, setEmailCopy] = useState(DEFAULT_EMAIL_COPY)
  const [selectedReviewFormId, setSelectedReviewFormId] = useState<
    string | null
  >(null)
  const [approvedTemplates, setApprovedTemplates] = useState<IFormTemplate[]>(
    [],
  )
  const [formsLoading, setFormsLoading] = useState(false)
  const [formsError, setFormsError] = useState<string | null>(null)
  const [patientScheduleRows, setPatientScheduleRows] = useState<
    SendingAppointmentDetail[] | null
  >(null)
  const [schedulesError, setSchedulesError] = useState<string | null>(null)
  const providedAppointmentsRef = useRef(appointments)
  providedAppointmentsRef.current = appointments

  const appointmentRowsForSchedule: SendingAppointmentDetail[] =
    isAddToAppointment || !patientId
      ? appointments
      : (patientScheduleRows ?? appointments)

  const schedules: AppointmentSchedule[] = useMemo(() => {
    return [...appointmentRowsForSchedule]
      .sort((a, b) => {
        const byDate = a.appointmentDate.localeCompare(b.appointmentDate)
        if (byDate !== 0) return byDate
        return a.appointmentId.localeCompare(b.appointmentId)
      })
      .map((apt) => ({
        id: apt.appointmentId,
        date: formatAppointmentLocalDate(
          { appt_date: apt.appointmentDate },
          'M/D/YYYY',
        ),
        type: apt.appointmentType,
      }))
  }, [appointmentRowsForSchedule])

  const selectableTemplates = approvedTemplates.filter(
    (t): t is IFormTemplate & { form_template_id: string } =>
      Boolean(t.form_template_id),
  )

  const templatesByCategory = selectableTemplates.reduce<
    Record<string, Array<IFormTemplate & { form_template_id: string }>>
  >((acc, t) => {
    const key = (t.category_title ?? '').trim() || 'Others'
    if (!acc[key]) acc[key] = []
    acc[key].push(t)
    return acc
  }, {})

  const sortedCategoryTitles = Object.keys(templatesByCategory).sort((a, b) => {
    const orderA = getCategorySortOrder(a)
    const orderB = getCategorySortOrder(b)
    if (orderA !== orderB) return orderA - orderB
    return a.localeCompare(b)
  })

  const selectedForms: FormData[] = approvedTemplates
    .filter(
      (t) => t.form_template_id && selectedFormIds.includes(t.form_template_id),
    )
    .map(formTemplateToFormData)

  useEffect(() => {
    if (!show) {
      setStep(isAddToAppointment || variant === 'direct' ? 2 : 1)
      setIsDirect(variant === 'direct')
      setSelectedScheduleIds([])
      setSendFormsTo('kiosk')
      setSelectedFormIds([])
      setEmailCopy(DEFAULT_EMAIL_COPY)
      setSelectedReviewFormId(null)
    }
  }, [show, isAddToAppointment, variant])

  useEffect(() => {
    if (!show) return
    setFormsLoading(true)
    setFormsError(null)
    getFormTemplates({ status: FormTemplateStatus.Approved })
      .then((templates) => {
        setApprovedTemplates(templates ?? [])
      })
      .catch(() => {
        setFormsError('Failed to load forms.')
        setApprovedTemplates([])
      })
      .finally(() => {
        setFormsLoading(false)
      })
  }, [show])

  useEffect(() => {
    if (!show || !initialSelectedFormId) return
    const hasTemplate = approvedTemplates.some(
      (template) => template.form_template_id === initialSelectedFormId,
    )
    if (!hasTemplate) return
    setSelectedFormIds((prev) =>
      prev.includes(initialSelectedFormId) ? prev : [initialSelectedFormId],
    )
  }, [show, initialSelectedFormId, approvedTemplates])

  useEffect(() => {
    if (!show || isAddToAppointment || variant === 'direct' || !patientId) {
      setPatientScheduleRows(null)
      setSchedulesError(null)
      return
    }

    let cancelled = false
    setPatientScheduleRows(null)
    setSchedulesError(null)
    const start = moment().format('YYYY-MM-DD')
    const end = moment()
      .add(SCHEDULE_LOOKAHEAD_DAYS, 'days')
      .format('YYYY-MM-DD')
    void getAppointmentsSummary(start, end)
      .then((res) => {
        if (cancelled) return
        const rows = (res?.data?.appointments ?? [])
          .filter(
            (apt) => apt.patient_id === patientId && isUpcomingAppointment(apt),
          )
          .map(summaryToScheduleRow)
        setPatientScheduleRows(rows)
      })
      .catch(() => {
        if (cancelled) return
        const fallback = providedAppointmentsRef.current
        if (fallback.length > 0) {
          setPatientScheduleRows(fallback)
          return
        }
        setPatientScheduleRows([])
        setSchedulesError('Failed to load schedules.')
      })

    return () => {
      cancelled = true
    }
  }, [show, isAddToAppointment, patientId, variant])

  useEffect(() => {
    if (step === 3 && selectedForms.length > 0) {
      const isCurrentValid = selectedReviewFormId
        ? selectedForms.some((f) => f.id === selectedReviewFormId)
        : false
      if (!isCurrentValid) {
        setSelectedReviewFormId(selectedForms[0].id)
      }
    }
  }, [step, selectedForms, selectedReviewFormId])

  const handleScheduleSelect = (id: string): void => {
    setIsDirect(id === 'direct')
    setSelectedScheduleIds(id === 'direct' ? [] : [id])
  }

  const handleFormToggle = (id: string): void => {
    setSelectedFormIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  const handleNext = (): void => {
    if (step < 3) setStep(step + 1)
  }

  const handlePrevious = (): void => {
    if (step > 1) setStep(step - 1)
  }

  const handleCancel = (): void => {
    if (sending) return
    onHide()
  }

  const handleCreatePatientForm = (): void => {
    onHide()
    navigate('/form-builder/create', {
      state: { formTitle: PATIENT_SPECIFIC_FORM_TITLE },
    })
  }

  const handleSendForms = (): void => {
    const recipient: 'kiosk' | 'mobile' =
      sendFormsTo === 'kiosk' ? 'kiosk' : 'mobile'
    if (
      !patientId ||
      (!isDirect && selectedScheduleIds.length === 0) ||
      selectedFormIds.length === 0
    ) {
      showToast(
        createFailedToast(
          'Error',
          'Please select an appointment and at least one form before sending.',
        ),
      )
      return
    }
    if (sending) return
    if (sendFormsTo === 'mobile' && !patientMobile?.trim()) {
      showToast(
        createFailedToast(
          'Missing mobile number',
          'Add a mobile number to this patient before sending an invitation.',
        ),
      )
      return
    }
    const finish = (assignmentIds: string[]): void => {
      const entry = createAssignedFormEntry({
        patientId,
        publicPatientId,
        patientName,
        appointments: appointmentRowsForSchedule,
        selectedScheduleIds,
        selectedForms,
        assignmentIds: selectedForms.map(
          (form) => assignmentIds[selectedFormIds.indexOf(form.id)],
        ),
      })
      onFormsAssigned?.(entry)
      onHide()
    }
    const refreshRequestsAfterFailure = (): void => {
      void getPendingFormEntries({ patient_id: patientId })
        .then((entries) => {
          const entry = entries.find((row) => row.patientId === patientId)
          if (entry) onFormsAssigned?.(entry)
        })
        .catch(() => {
          /* Keep the send error visible; a later refresh can retry loading. */
        })
    }
    setSending(true)
    showLoading({ container: '.page-content' })

    if (sendFormsTo === 'kiosk') {
      let assignmentIds: string[] = []
      createFormAssignments({
        patient_id: patientId,
        appointment_ids: selectedScheduleIds,
        form_template_ids: selectedFormIds,
      })
        .then((result) => {
          assignmentIds = result.assignmentIds
          return createPatientFormsOtp({
            patient_id: patientId,
            recipient: 'kiosk',
            ...(selectedScheduleIds[0] != null &&
              selectedScheduleIds[0] !== '' && {
                appointment_id: selectedScheduleIds[0],
              }),
          })
        })
        .then((otpResult) => {
          hideLoading('.page-content')
          finish(assignmentIds)
          if (onOTPShow && otpResult.code) {
            onOTPShow(
              patientName,
              otpResult.code,
              otpResult.otp_id,
              selectedScheduleIds[0],
            )
          } else {
            showToast(createKioskOtpGeneratedToast())
          }
        })
        .catch(() => {
          refreshRequestsAfterFailure()
          hideLoading('.page-content')
          showToast(
            createFailedToast(
              'Error',
              'Failed to create kiosk OTP. The form request remains pending; retry to generate access.',
            ),
          )
        })
        .finally(() => setSending(false))
      return
    }

    if (sendFormsTo === 'email') {
      setSending(false)
      hideLoading('.page-content')
      showToast(
        createSuccessToast(
          'Email disabled',
          'Email delivery is currently disabled. Please use mobile invitation.',
        ),
      )
      return
    }

    sendFormAssignmentsInvitation({
      patient_id: patientId,
      appointment_ids: selectedScheduleIds,
      form_template_ids: selectedFormIds,
      channel: recipient,
      ...(sendFormsTo === 'mobile' &&
        patientMobile && {
          recipient_phone: patientMobile,
        }),
    })
      .then((result) => {
        hideLoading('.page-content')
        finish(result.assignmentIds)
        showToast(createFormsSentToast())
      })
      .catch(() => {
        refreshRequestsAfterFailure()
        hideLoading('.page-content')
        showToast(
          createFailedToast(
            'Error',
            'Failed to send invitation. Retry sending to reuse any pending form request.',
          ),
        )
      })
      .finally(() => setSending(false))
  }

  const canProceedStep1 = isDirect || selectedScheduleIds.length > 0
  const canProceedStep2 = selectedFormIds.length > 0

  const renderStepContent = (): React.ReactNode => {
    if (step === 1) {
      const scheduleOptions = [
        { value: 'direct', label: 'Direct request (no appointment)' },
        ...schedules.map((schedule) => ({
          value: schedule.id,
          label: `${schedule.date} - ${schedule.type}`,
        })),
      ]
      const isSchedulesLoading =
        patientScheduleRows === null && appointments.length === 0

      return (
        <div className="assign-forms-step assign-forms-step-schedule">
          <div className="assign-forms-step-schedule-layout">
            <div className="assign-forms-schedule-field">
              <div className="assign-forms-schedule-label">
                Scheduled appointments:{' '}
                <span className="assign-forms-patient-name">{patientName}</span>
              </div>
              {isSchedulesLoading ? (
                <div className="assign-forms-schedule-loading">
                  Loading schedules...
                </div>
              ) : schedulesError ? (
                <div className="assign-forms-forms-error" role="alert">
                  {schedulesError}
                </div>
              ) : (
                <Select
                  label="Scheduled appointments"
                  placeholder="Select"
                  options={scheduleOptions}
                  value={isDirect ? 'direct' : (selectedScheduleIds[0] ?? '')}
                  onChange={(value) => {
                    if (typeof value === 'string') {
                      handleScheduleSelect(value)
                    }
                  }}
                  className="assign-forms-schedule-select"
                />
              )}
            </div>
          </div>
        </div>
      )
    }

    if (step === 2) {
      const showEmailCopy = !isAddToAppointment && sendFormsTo !== 'kiosk'
      const previewCopy =
        sendFormsTo === 'mobile'
          ? isDirect
            ? `Please review the forms requested by your dental clinic through our secure portal.

These forms are view-only and do not require signing in the portal.

• Link: [Secure Link]
• Enter invitation code: [XXXXXX]

This invitation is valid for 14 days.`
            : DEFAULT_SMS_COPY
          : emailCopy
      const previewCopyLabel =
        sendFormsTo === 'mobile' ? 'SMS Content:' : 'Email Content Copy:'
      const formsSectionTitle = 'Select forms to be sent'
      return (
        <div className="assign-forms-step assign-forms-step-forms">
          <div className="assign-forms-step-forms-layout">
            {!isAddToAppointment && (
              <RadioGroup
                name="send-forms-to"
                label="Send forms to:"
                orientation="horizontal"
                className="assign-forms-send-to-section"
                value={sendFormsTo}
                onChange={(value) => setSendFormsTo(value as SendFormsTo)}
                options={[
                  { value: 'kiosk', label: 'Kiosk' },
                  { value: 'mobile', label: "Patient's mobile no." },
                  { value: 'email', label: "Patient's email", disabled: true },
                ]}
              />
            )}

            <div className="assign-forms-step-forms-bottom-row">
              {showEmailCopy && (
                <div className="assign-forms-step-forms-email">
                  <div className="assign-forms-email-section">
                    <div className="assign-forms-section-label">
                      {previewCopyLabel}
                    </div>
                    <div className="assign-forms-email-copy">
                      <pre>
                        {(() => {
                          const [beforeSecure, afterSecure = ''] =
                            previewCopy.split('[Secure Link]')
                          const [beforeOtp, afterOtp = ''] =
                            afterSecure.split('[XXXXXX]')
                          return (
                            <>
                              {beforeSecure}
                              <span className="assign-forms-email-link">
                                [Secure Link]
                              </span>
                              {beforeOtp}
                              <span className="assign-forms-email-link">
                                [XXXXXX]
                              </span>
                              {afterOtp}
                            </>
                          )
                        })()}
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              <div className="assign-forms-step-forms-panel">
                <div className="assign-forms-section-label">
                  {formsSectionTitle}
                </div>
                <div className="assign-forms-forms-panel">
                  {formsLoading && (
                    <div className="assign-forms-forms-loading">
                      Loading forms…
                    </div>
                  )}
                  {!formsLoading && formsError && (
                    <div className="assign-forms-forms-error">{formsError}</div>
                  )}
                  {!formsLoading &&
                    !formsError &&
                    selectableTemplates.length === 0 && (
                      <div className="assign-forms-forms-empty">
                        No approved forms. Create one in Form Builder or approve
                        existing templates.
                      </div>
                    )}

                  {!formsLoading &&
                    !formsError &&
                    selectableTemplates.length > 0 && (
                      <Accordion
                        grouped
                        defaultOpenIds={
                          sortedCategoryTitles[0]
                            ? [sortedCategoryTitles[0]]
                            : undefined
                        }
                        className="assign-forms-accordion"
                      >
                        {sortedCategoryTitles.map((categoryTitle) => (
                          <AccordionItem
                            id={categoryTitle}
                            key={categoryTitle}
                            title={categoryTitle}
                            showInfo
                          >
                            <div className="assign-forms-form-list">
                              {(templatesByCategory[categoryTitle] ?? []).map(
                                (t) => (
                                  <Checkbox
                                    key={t.form_template_id}
                                    id={`form-${t.form_template_id}`}
                                    label={t.title}
                                    checked={selectedFormIds.includes(
                                      t.form_template_id,
                                    )}
                                    onChange={() =>
                                      handleFormToggle(t.form_template_id)
                                    }
                                  />
                                ),
                              )}
                            </div>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}

                  {SHOW_CREATE_PATIENT_SPECIFIC_FORM && (
                    <button
                      type="button"
                      className="assign-forms-create-patient-form"
                      onClick={handleCreatePatientForm}
                    >
                      <AddIcon
                        className="assign-forms-create-patient-form__icon"
                        aria-hidden
                      />
                      Create patient-specific form
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (step === 3) {
      const reviewTemplate = approvedTemplates.find(
        (t) => t.form_template_id === selectedReviewFormId,
      )
      const reviewSchema = reviewTemplate
        ? schemaJsonToGroupsWithError(reviewTemplate.schema_json)
        : { groups: [], error: null }
      const reviewGroups = reviewSchema.groups
      return (
        <div className="assign-forms-step assign-forms-step-review">
          <div className="assign-forms-review-container">
            <div className="assign-forms-review-left">
              <div className="assign-forms-selected-forms-header">
                Selected forms
              </div>
              {selectedForms.length === 0 ? (
                <div className="assign-forms-no-forms">No forms selected</div>
              ) : (
                selectedForms.map((form) => (
                  <button
                    key={form.id}
                    type="button"
                    className={`assign-forms-review-form-item${
                      selectedReviewFormId === form.id ? ' active' : ''
                    }`}
                    aria-pressed={selectedReviewFormId === form.id}
                    onClick={() => setSelectedReviewFormId(form.id)}
                  >
                    <span>{form.name}</span>
                    <ChevronRightIcon
                      className="assign-forms-review-form-item__icon"
                      aria-hidden
                    />
                  </button>
                ))
              )}
            </div>
            <div className="assign-forms-review-right">
              {reviewTemplate ? (
                <>
                  {reviewSchema.error && (
                    <div className="assign-forms-forms-error" role="alert">
                      Could not render this form preview. {reviewSchema.error}
                    </div>
                  )}
                  <SchemaFormRenderer
                    groups={reviewGroups}
                    formTitle={reviewTemplate.title ?? 'Untitled form'}
                    readOnly
                  />
                </>
              ) : (
                <div className="assign-forms-no-form-selected">
                  Select a form to preview
                </div>
              )}
            </div>
          </div>
        </div>
      )
    }

    return null
  }

  const handleAddFormsToAppointment = (): void => {
    const titleById: Record<string, string> = {}
    for (const id of selectedFormIds) {
      const t = approvedTemplates.find((x) => x.form_template_id === id)
      titleById[id] = (t?.title ?? '').trim() || 'Untitled form'
    }
    onAddFormsToAppointment?.(selectedFormIds, titleById)
    onHide()
  }

  const renderFooter = (): React.ReactNode => {
    if (isAddToAppointment) {
      return (
        <div className="assign-forms-footer-buttons">
          <Button variant="secondary" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={step === 2 ? handleCancel : handlePrevious}
          >
            Back
          </Button>
          {step < 3 ? (
            <Button
              variant="primary"
              size="sm"
              onClick={handleNext}
              disabled={step === 2 && !canProceedStep2}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddFormsToAppointment}
              disabled={selectedFormIds.length === 0}
            >
              Add forms to appointment
            </Button>
          )}
        </div>
      )
    }

    if (step < 3) {
      const goesBack = step === 2 && variant !== 'direct'
      return (
        <div className="assign-forms-footer-buttons">
          <Button
            variant="secondary"
            size="sm"
            onClick={goesBack ? handlePrevious : handleCancel}
          >
            {goesBack ? 'Back' : 'Cancel'}
          </Button>
          {SHOW_CREATE_PATIENT_SPECIFIC_FORM && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCreatePatientForm}
            >
              Create patient-specific form
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={handleNext}
            disabled={
              (step === 1 && !canProceedStep1) ||
              (step === 2 && !canProceedStep2)
            }
          >
            Next
          </Button>
        </div>
      )
    }
    return (
      <div className="assign-forms-footer-buttons">
        <Button variant="secondary" size="sm" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="secondary" size="sm" onClick={handlePrevious}>
          Previous
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSendForms}
          disabled={sending || !canProceedStep2}
        >
          {sending ? 'Sending…' : 'Send forms'}
        </Button>
      </div>
    )
  }

  const modalClassName = [
    'assign-forms-modal',
    isAddToAppointment && 'assign-forms-modal--add-to-appointment',
    !isAddToAppointment && step !== 3 && 'assign-forms-modal--assign-wizard',
    step === 3 && 'assign-forms-modal--review',
    !isAddToAppointment && step === 1 && 'assign-forms-modal--schedule-step',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Modal
      open={show}
      onClose={handleCancel}
      size={step === 3 ? 'lg' : 'md'}
      closeOnBackdrop={false}
      title={
        variant === 'direct' ? `Send forms to ${patientName}` : 'Assign forms'
      }
      className={modalClassName}
      bodyClassName="assign-forms-modal-body"
      secondaryLabel=""
      primaryLabel=""
      footerStart={renderFooter()}
    >
      <div className="assign-forms-stepper-wrap">
        <Stepper
          steps={variant === 'direct' ? STEPS.slice(1) : STEPS}
          currentStep={variant === 'direct' ? step - 2 : step - 1}
        />
      </div>
      <div className="assign-forms-step-content">{renderStepContent()}</div>
    </Modal>
  )
}

export default AssignFormsModal
